import {
  convertPxToRem,
  formatAmountDecimal,
  formatDateToReadableString,
  formatDateToReadableTime,
  getShortDayFromDate,
  getTimeZoneFromDate,
} from "@/utils";
import Panel from "../Panel";
import IconLoader from "../IconLoader";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import theme, { defaultFullscreenPageStyling } from "@/theme";
import CircledBackButton from "../CircledBackButton";
import {
  MembershipDependent,
  OneVoucherDetails,
  PaymentMethod,
} from "@/services/core-api-adapter";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";

interface PaymentReceiptProps {
  amount: number;
  currency: string;
  transactionDate: string;
  referenceNumber: string;
  merchantId: string;
  cardNumber: string;
  isDiscountApplied?: boolean;
  originalAmount?: number;
  voucherCode?: string;
  voucherTypeName?: string;
  paymentMethod?: PaymentMethod;
  oneVoucherDetails?: OneVoucherDetails | null;
  onBackButtonClick: () => void;
}

export default function PaymentReceipt({
  amount,
  currency,
  transactionDate,
  referenceNumber,
  merchantId,
  cardNumber,
  isDiscountApplied = false,
  originalAmount,
  voucherCode,
  voucherTypeName,
  paymentMethod,
  oneVoucherDetails,
  onBackButtonClick,
}: PaymentReceiptProps) {
  const { t } = useTranslation();

  const amountFormatted = formatAmountDecimal(amount, currency);
  const formattedTransactionDate = `${getShortDayFromDate(new Date(transactionDate))}, ${formatDateToReadableString(new Date(transactionDate))}`;
  const formattedTransactionTime = formatDateToReadableTime(transactionDate);
  const formattedTransactionTimeZone = getTimeZoneFromDate(
    new Date(transactionDate)
  );
  return (
    <Stack spacing={5} sx={{ ...defaultFullscreenPageStyling }}>
      <CircledBackButton showLabel onClick={onBackButtonClick} />
      <Panel sx={{ paddingBottom: 4 }}>
        <Stack justifyContent="center" alignItems="center" spacing={4}>
          <IconLoader
            icon="UnuLogo"
            color="primary"
            sx={{ fontSize: convertPxToRem(90) }}
          />
          <Stack
            direction="row"
            width="80%"
            justifyContent="space-between"
            color="neutral.main"
          >
            <Typography variant="subtitle1" color="inherit" fontWeight={400}>
              {formattedTransactionDate}
            </Typography>
            <Divider
              sx={{ borderColor: theme.palette.neutral.main }}
              orientation="vertical"
              flexItem
            />
            <Typography variant="subtitle1" color="inherit" fontWeight={400}>
              {formattedTransactionTime}
            </Typography>
            <Divider
              sx={{ borderColor: theme.palette.neutral.main }}
              orientation="vertical"
              flexItem
            />
            <Typography variant="subtitle1" color="inherit" fontWeight={400}>
              {formattedTransactionTimeZone}
            </Typography>
          </Stack>
          <Divider sx={{ width: "100%" }} />
          <Stack alignItems="center">
            <IconLoader
              icon="TrolleyWithCheckIcon"
              color="primary"
              sx={{ fontSize: convertPxToRem(90) }}
            />
            <Typography variant="h2">{t("PaymentReceipt.title")}</Typography>
            <Typography variant="h2" color="primary">
              {amountFormatted}
            </Typography>
            {isDiscountApplied && originalAmount && (
              <Typography
                variant="body2"
                color="neutral.main"
                sx={{ textDecoration: "line-through" }}
              >
                (
                {t("common.discount.prefix", {
                  amountFormatted: formatAmountDecimal(
                    originalAmount,
                    currency
                  ),
                })}
                )
              </Typography>
            )}
          </Stack>
          <Stack textAlign="left" width="100%" spacing={2}>
            <Box>
              <Typography variant="subtitle2">
                {t("PaymentReceipt.referenceNumber.title")}
              </Typography>
              <Typography variant="h4">{referenceNumber}</Typography>
            </Box>
            {!isFeatureEnabled(
              FeatureToggleIdentifier.ENABLE_ENHANCED_PAYMENT_OPTIONS
            ) ? (
              <>
                {(!voucherCode || merchantId) && (
                  <Box>
                    <Typography variant="subtitle2">
                      {t("PaymentReceipt.merchantId.title")}
                    </Typography>
                    <Typography variant="h4">{merchantId}</Typography>
                  </Box>
                )}
                {(!voucherCode || cardNumber) && (
                  <Box>
                    <Typography variant="subtitle2">
                      {t("PaymentReceipt.cardNumber.title")}
                    </Typography>
                    <Typography variant="h4">{cardNumber}</Typography>
                  </Box>
                )}

                {(!merchantId || voucherTypeName) && (
                  <Box>
                    <Typography variant="subtitle2">
                      {t("PaymentReceipt.voucherName.title")}
                    </Typography>
                    <Typography variant="h4">{voucherTypeName}</Typography>
                  </Box>
                )}

                {(!cardNumber || voucherCode) && (
                  <Box>
                    <Typography variant="subtitle2">
                      {t("PaymentReceipt.voucherCode.title")}
                    </Typography>
                    <Typography variant="h4">{voucherCode}</Typography>
                  </Box>
                )}
              </>
            ) : (
              <>
                {merchantId && (
                  <Box>
                    <Typography variant="subtitle2">
                      {t("PaymentReceipt.merchantId.title")}
                    </Typography>
                    <Typography variant="h4">{merchantId}</Typography>
                  </Box>
                )}
                {paymentMethod === PaymentMethod.OneVoucher && (
                  <>
                    <Box>
                      <Typography variant="subtitle2">
                        {t("PaymentReceipt.paymentType.title")}
                      </Typography>
                      <Typography variant="h4">
                        {t("PaymentReceipt.oneVoucher.title")}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2">
                        {t("PaymentReceipt.voucherPin.title")}
                      </Typography>
                      <Typography variant="h4">
                        {oneVoucherDetails?.voucherPin}
                      </Typography>
                    </Box>
                  </>
                )}
                {paymentMethod === PaymentMethod.EcentricEFT && (
                  <>
                    <Box>
                      <Typography variant="subtitle2">
                        {t("PaymentReceipt.paymentType.title")}
                      </Typography>
                      <Typography variant="h4">
                        {t("PaymentReceipt.instantEFT.title")}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2">
                        {t("PaymentReceipt.accNr.title")}
                      </Typography>
                      <Typography variant="h4">{cardNumber}</Typography>
                    </Box>
                  </>
                )}
                {paymentMethod === PaymentMethod.CardNotPresent && (
                  <>
                    <Box>
                      <Typography variant="subtitle2">
                        {t("PaymentReceipt.paymentType.title")}
                      </Typography>
                      <Typography variant="h4">
                        {t("PaymentReceipt.card.title")}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2">
                        {t("PaymentReceipt.cardNumber.title")}
                      </Typography>
                      <Typography variant="h4">{cardNumber}</Typography>
                    </Box>
                  </>
                )}
              </>
            )}
          </Stack>
          <Divider sx={{ width: "100%" }} />
          <Typography variant="subtitle1" color="neutral.main">
            {t("PaymentReceipt.footer.title")}
          </Typography>
        </Stack>
      </Panel>
    </Stack>
  );
}
